import {Component, EventEmitter, OnInit, Output, ViewChild} from '@angular/core';
import {EventInput} from '@fullcalendar/core';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {DataService} from '../../../Services/data.service';
import {SysUserModel} from '../../../DataModels/sys-user-model';
import {NotificationService} from '../../../Services/notification.service';
import {CalendarDto} from '../../../DataModels/calendar-event-model';


@Component({
  selector: 'app-calendar-event',
  templateUrl: './event.component.html',
  styleUrls: ['./event.component.scss'],
  standalone: false
})
export class EventComponent implements OnInit {

  event: any = {};
  descriptionUrl: string = '';
  modelTitle: string = "";
  users: SysUserModel[] = [];
  usersLoaded: boolean = false;

  calendars: CalendarDto[] = [];
  calendarsLoaded: boolean = false;

  priorities: any[] = [
    {label: 'Priority 1', value: 'danger'},
    {label: 'Priority 2', value: 'warning'},
    {label: 'Priority 3', value: 'primary'},
    {label: 'Priority 4', value: 'success'}
  ];

  categories: string[] = ['Support', 'Accounting', 'Operations', 'General', 'Workorder'];

  days: any[] = [
    {label: 'Sun', value: 0},
    {label: 'Mon', value: 1},
    {label: 'Tue', value: 2},
    {label: 'Wed', value: 3},
    {label: 'Thu', value: 4},
    {label: 'Fri', value: 5},
    {label: 'Sat', value: 6}
  ];


  @Output() eventSaved: EventEmitter<EventInput> = new EventEmitter();
  @Output() eventDeleted: EventEmitter<EventInput> = new EventEmitter();
  @Output() inviteResponded: EventEmitter<void> = new EventEmitter();

  respondingToInvite: boolean = false;

  @ViewChild('content', { static: true }) content: any;

  constructor(public activeModal: NgbModal, private dataService: DataService, private notificationService: NotificationService) {
  }

  isArray(obj: any): boolean {
    return Array.isArray(obj);
  }


  ngOnInit(): void {
    this.loadUsers();
    this.loadCalendars();
  }

  loadCalendars() {
    if (this.calendarsLoaded) {
      return;
    }
    this.dataService.getData('Calendar/calendars').subscribe((result: any) => {
      const raw = Array.isArray(result) ? result : (result?.Data ?? []);
      this.calendars = raw.map((c: any) => ({
        id: c.Id ?? c.id ?? null,
        name: c.Name ?? c.name ?? '',
        ownerId: c.OwnerId ?? c.ownerId ?? null,
        color: c.Color ?? c.color ?? null,
        isDefault: c.IsDefault ?? c.isDefault ?? false
      }));
      this.calendarsLoaded = true;
    });
  }

  loadUsers() {
    // console.log('loadUsers called, current users length:', this.users?.length);
    // If users are already passed from parent and they look valid, we can skip.
    if (this.users && this.users.length > 0 && this.usersLoaded) {
      // console.log('Users already loaded or passed by parent.');
      return;
    }

    this.dataService.getData('sys_user').subscribe((result: any) => {
      // console.log('sys_user API result:', result);

      let usersArray: SysUserModel[] = [];
      if (result && Array.isArray(result.Data)) {
        usersArray = result.Data;
      } else if (Array.isArray(result)) {
        usersArray = result;
      }

      if (usersArray.length > 0) {
        this.users = usersArray;
        this.usersLoaded = true;
      }
      // console.log('Loaded users count:', this.users.length);
    });
  }

  /**
   * opens modal
   * @param title title of modal
   * @param data data to be used in modal
   */
  openModal(title: string, data: any): void {
    this.modelTitle = title;
    this.respondingToInvite = false;
    this.loadUsers();
    this.loadCalendars();

    const dow = data.DaysOfWeek;
    let daysOfWeekArr: number[] = [];
    if (Array.isArray(dow)) {
      daysOfWeekArr = dow;
    } else if (typeof dow === 'string' && dow) {
      daysOfWeekArr = dow.split(',').map((d: any) => parseInt(d));
    }

    const formatDateTime = (date: any) => {
      if (!date) {
        return '';
      }
      if (typeof date === 'string') {
        if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(date)) {
          return date;
        }
        if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(date)) {
          return date.slice(0, 16);
        }
      }
      const d = (date instanceof Date) ? date : new Date(date);
      if (isNaN(d.getTime())) {
        return '';
      }
      const tzOffset = d.getTimezoneOffset() * 60000;
      return new Date(d.getTime() - tzOffset).toISOString().slice(0, 16);
    };

    const formatDateOnly = (date: any) => {
      if (!date) {
        return '';
      }
      if (typeof date === 'string') {
        if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
          return date;
        }
        if (/^\d{4}-\d{2}-\d{2}T/.test(date)) {
          return date.split('T')[0];
        }
      }
      const d = (date instanceof Date) ? date : new Date(date);
      if (isNaN(d.getTime())) {
        return '';
      }
      const tzOffset = d.getTimezoneOffset() * 60000;
      return new Date(d.getTime() - tzOffset).toISOString().split('T')[0];
    };

    const rawDescription: string = data.ExtendedProps?.Description || '';
    const urlMatch = rawDescription.match(/(https?:\/\/[^\s]+)/);
    this.descriptionUrl = urlMatch ? urlMatch[0] : '';
    const cleanDescription = rawDescription.replace(/(https?:\/\/[^\s]+)/g, '').replace(/\n+$/, '').trimEnd();

    this.event = {
      Id: data.Id,
      Title: data.Title,
      Description: cleanDescription,
      Category: data.ExtendedProps?.Category || 'General',
      Priority: data.ExtendedProps?.Priority || 'primary',
      Start: formatDateTime(data.Start),
      End: formatDateTime(data.End),
      AllDay: data.AllDay || false,
      ClassNames: data.ClassNames,
      DaysOfWeek: daysOfWeekArr,
      StartTime: data.StartTime || '',
      EndTime: data.EndTime || '',
      StartRecur: formatDateOnly(data.StartRecur),
      EndRecur: formatDateOnly(data.EndRecur),
      isRecurring: false,
      Attendees: Array.isArray(data.Attendees) ? [...data.Attendees] : [],
      IsOrganizer: data.ExtendedProps?.IsOrganizer !== undefined ? data.ExtendedProps.IsOrganizer : true,
      MyStatus: data.ExtendedProps?.MyStatus || 'Organizer',
      OrganizerId: data.OrganizerId || data.ExtendedProps?.OrganizerId,
      calendarId: data.calendarId ?? data.extendedProps?.calendarId ?? null,
      isPrivate: data.isPrivate ?? data.extendedProps?.isPrivate ?? false
    };

    // If data was received as raw strings, they were already handled by formatDateTime/formatDateOnly above
    // based on how data.Start etc were passed into the method.

    // Robustly detect if it should be recurring
    if (this.event.DaysOfWeek?.length > 0 ||
      (this.event.StartTime && this.event.StartTime !== '00:00:00') ||
      (this.event.EndTime && this.event.EndTime !== '00:00:00') ||
      this.event.StartRecur ||
      this.event.EndRecur) {
      this.event.isRecurring = true;
    }

    // console.log('Event initialized in modal:', JSON.stringify(this.event, null, 2));
    this.activeModal.open(this.content, {backdrop: 'static'});
  }

  toggleDay(dayValue: number) {
    if (!this.event.DaysOfWeek) {
      this.event.DaysOfWeek = [];
    }
    const index = this.event.DaysOfWeek.indexOf(dayValue);
    if (index > -1) {
      this.event.DaysOfWeek.splice(index, 1);
    } else {
      this.event.DaysOfWeek.push(dayValue);
    }
  }

  isDaySelected(dayValue: number): boolean {
    return this.event.DaysOfWeek && this.event.DaysOfWeek.includes(dayValue);
  }

  onIsRecurringChange() {
    if (!this.event.isRecurring) {
      // console.log('isRecurring changed to false. Clearing recurring fields.');
      this.event.DaysOfWeek = null;
      this.event.StartRecur = null;
      this.event.EndRecur = null;
    } else {
      // Prime StartRecur with the date from Start if StartRecur is empty
      if (!this.event.StartRecur && this.event.Start) {
        // Extract YYYY-MM-DD from YYYY-MM-DDTHH:mm
        this.event.StartRecur = this.event.Start.split('T')[0];
      }
    }
  }

  isAttendeeSelected(userId: any): boolean {
    if (!this.event.Attendees || !Array.isArray(this.event.Attendees)) {
      return false;
    }
    return this.event.Attendees.some((a: any) =>
      (a.UserId === userId)
    );
  }

  toggleAttendee(user: SysUserModel) {
    if (!Array.isArray(this.event.Attendees)) {
      this.event.Attendees = [];
    }

    const selectedId = user.id;
    const index = this.event.Attendees.findIndex((a: any) =>
      (a.UserId === selectedId)
    );

    if (index > -1) {
      // Remove attendee
      this.event.Attendees = [
        ...this.event.Attendees.slice(0, index),
        ...this.event.Attendees.slice(index + 1)
      ];
    } else {
      // Add attendee
      const attendee = {
        UserId: selectedId,
        Name: user.name,
        Status: 'Pending'
      };
      this.event.Attendees = [...this.event.Attendees, attendee];
    }
  }

  removeAttendee(attendee: any) {
    const index = this.event.Attendees.indexOf(attendee);
    if (index > -1) {
      this.event.Attendees = [
        ...this.event.Attendees.slice(0, index),
        ...this.event.Attendees.slice(index + 1)
      ];
    }
  }

  /**
   * stores event in calendar events
   */
  saveEvent() {
    if (this.event.isRecurring && this.event.StartRecur && this.event.Start) {
      const startRecurDate = this.event.StartRecur; // YYYY-MM-DD
      const startDate = this.event.Start.split('T')[0]; // YYYY-MM-DD

      if (startRecurDate < startDate) {
        this.notificationService.warning('Recurrence Start Date cannot be earlier than the Event Start Date.');
        return;
      }
    }

    if (!this.event.isRecurring) {
      this.event.DaysOfWeek = null;
      this.event.StartRecur = null;
      this.event.EndRecur = null;
    }
    const savedEvent = {...this.event};
    if (this.descriptionUrl) {
      savedEvent.Description = (savedEvent.Description ? savedEvent.Description + '\n\n' : '') + this.descriptionUrl;
    }
    this.eventSaved.emit(savedEvent);
    this.activeModal.dismissAll();
  }

  /**
   * deletes event from calendar events
   */
  deleteEvent() {
    this.eventDeleted.emit(this.event);
    this.activeModal.dismissAll();
  }

  respondToInvite(status: string): void {
    if (this.respondingToInvite) {
      return;
    }
    this.respondingToInvite = true;
    this.dataService.post('Calendar/RespondToInvitation', {
      eventId: this.event.Id || this.event.id,
      status,
      responseMessage: null
    }).subscribe({
      next: () => {
        this.event.MyStatus = status;
        this.respondingToInvite = false;
        this.inviteResponded.emit();
        this.activeModal.dismissAll();
      },
      error: () => {
        this.respondingToInvite = false;
      }
    });
  }

  statusBadgeClass(status: string): string {
    switch (status) {
      case 'Accepted':
        return 'bg-success';
      case 'Declined':
        return 'bg-danger';
      case 'Tentative':
        return 'bg-warning text-dark';
      default:
        return 'bg-secondary';
    }
  }

}
